import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categories, articles, testimonials } from '@/mocks/articles';
import { useState, useEffect } from 'react';

const categoryImages: Record<string, string> = {
  'star-collapse': 'https://readdy.ai/api/search-image?query=Dramatic%20celebrity%20scandal%20scene%20with%20paparazzi%20flashes%2C%20dark%20red%20carpet%20atmosphere%2C%20breaking%20news%20mood%2C%20cinematic%20lighting%2C%20dramatic%20shadows%2C%20entertainment%20industry%20controversy%2C%20warm%20orange%20and%20deep%20red%20tones%2C%20editorial%20photography%20style&width=1200&height=600&seq=cat-star-collapse&orientation=landscape',
  'wanghong-expose': 'https://readdy.ai/api/search-image?query=Social%20media%20influencer%20scandal%20scene%20with%20smartphone%20screens%2C%20dramatic%20dark%20lighting%2C%20modern%20digital%20aesthetic%2C%20warm%20amber%20and%20dark%20gray%20tones%2C%20investigative%20journalism%20mood%2C%20cinematic%20composition&width=1200&height=600&seq=cat-wanghong-expose&orientation=landscape',
  'daily-contest': 'https://readdy.ai/api/search-image?query=Trending%20viral%20topics%20with%20fire%20and%20sparks%20visual%20effects%2C%20entertainment%20news%20composition%2C%20dramatic%20orange%20and%20warm%20lighting%2C%20cinematic%20atmosphere%2C%20dynamic%20energy%2C%20social%20media%20trending%20aesthetic&width=1200&height=600&seq=cat-daily-contest&orientation=landscape',
  'video-gossip': 'https://readdy.ai/api/search-image?query=Cinematic%20video%20production%20scene%20with%20camera%20equipment%20and%20monitors%2C%20dark%20studio%20atmosphere%2C%20dramatic%20lighting%20with%20green%20and%20warm%20accents%2C%20professional%20film%20making%20environment&width=1200&height=600&seq=cat-video-gossip&orientation=landscape',
  'exclusive-reveal': 'https://readdy.ai/api/search-image?query=Mysterious%20investigative%20scene%20with%20documents%20and%20magnifying%20glass%2C%20dark%20purple%20and%20violet%20atmosphere%2C%20dramatic%20shadows%2C%20secretive%20mood%2C%20cinematic%20noir%20style%2C%20high%20contrast%20lighting&width=1200&height=600&seq=cat-exclusive-reveal&orientation=landscape',
  'hot-tracking': 'https://readdy.ai/api/search-image?query=Radar%20tracking%20and%20surveillance%20theme%20with%20dark%20tech%20aesthetic%2C%20slate%20gray%20and%20cool%20blue%20tones%2C%20investigative%20mood%2C%20modern%20digital%20tracking%20interface%20feel%2C%20cinematic%20composition&width=1200&height=600&seq=cat-hot-tracking&orientation=landscape',
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const categoryArticles = articles.filter((a) => a.category === slug);
  const [reviewIndex, setReviewIndex] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!category) {
    return (
      <>
        <Navbar />
        <div className="flex items-center justify-center h-96">
          <p className="text-foreground-400 text-lg">分类不存在</p>
        </div>
        <Footer />
      </>
    );
  }

  const t = testimonials[reviewIndex % testimonials.length];

  const schemaData = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebPage',
        name: `${category.name} - 51今日吃瓜网`,
        description: category.description,
        url: `https://www.51chigua.com/category/${slug}`,
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.51chigua.com/' },
          { '@type': 'ListItem', position: 2, name: category.name },
        ],
      },
      {
        '@type': 'ItemList',
        itemListElement: categoryArticles.map((article, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          item: {
            '@type': 'Article',
            name: article.title,
            url: `https://www.51chigua.com/article/${article.slug}`,
          },
        })),
      },
    ],
  };

  return (
    <>
      <SEOHead
        title={`${category.name} - 51今日吃瓜网`}
        description={category.description}
        keywords={`51吃瓜${category.name},${category.name}爆料,${category.name}最新,吃瓜${category.name}`}
        canonicalUrl={`/category/${slug}`}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
      />
      <Navbar />

      <main>
        {/* Category Switcher Bar */}
        <section className="bg-background-100 pt-20 pb-3">
          <div className="mx-auto max-w-[1280px] px-6 md:px-10">
            <div className="flex items-center gap-1.5 overflow-x-auto" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                    cat.slug === slug
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-50 text-foreground-600 hover:bg-background-200 hover:text-foreground-900'
                  }`}
                >
                  <i className={`${cat.icon} text-xs`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-background-100 pb-10 md:pb-14">
          <div className="mx-auto max-w-[1280px] px-6 md:px-10">
            <Breadcrumb items={[{ name: category.name }]} />

            <div className="mt-6 flex flex-col lg:flex-row gap-8 lg:gap-16 items-start">
              <div className="flex-1">
                <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-black text-foreground-950 leading-tight">
                  {category.name}
                </h1>
                <p className="mt-3 text-foreground-500 text-sm md:text-base max-w-lg leading-relaxed">
                  {category.description}
                </p>
              </div>
            </div>

            <div className="mt-10 flex gap-4">
              <div className="w-[45%] rounded-xl overflow-hidden h-[220px] md:h-[280px]">
                <img
                  src={categoryImages[slug] || categoryImages['star-collapse']}
                  alt={category.name}
                  title={category.name}
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="w-[55%] rounded-xl overflow-hidden h-[220px] md:h-[280px]">
                <img
                  src={categoryImages[slug] || categoryImages['star-collapse']}
                  alt={`${category.name}爆料追踪`}
                  title={`${category.name}最新动态`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-50">
          <div className="mx-auto max-w-[1280px] px-6 md:px-10">
            <div className="flex items-end justify-between mb-10">
              <div>
                <h2 className="font-heading text-3xl md:text-4xl font-black text-foreground-950">
                  {category.name}最新
                </h2>
                <p className="mt-2 text-foreground-500 text-sm md:text-base">共 {categoryArticles.length} 篇相关爆料</p>
              </div>
            </div>

            {categoryArticles.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
                {categoryArticles.map((article) => (
                  <Link
                    key={article.slug}
                    to={`/article/${article.slug}`}
                    className="group cursor-pointer"
                  >
                    <div className="relative rounded-xl overflow-hidden h-[340px]">
                      <img
                        src={article.thumbUrl}
                        alt={article.title}
                        title={article.title}
                        className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
                      {article.badge && (
                        <span className="absolute top-3 right-3 px-2.5 py-0.5 bg-accent-500 text-white text-xs font-bold rounded-full whitespace-nowrap">
                          {article.badge}
                        </span>
                      )}
                      <div className="absolute bottom-0 left-0 right-0 p-4">
                        <h3 className="font-heading text-base font-bold text-white leading-snug mb-1 line-clamp-2">
                          {article.title}
                        </h3>
                        <p className="text-white/60 text-xs line-clamp-1">{article.excerpt}</p>
                        <div className="flex items-center gap-3 mt-2 text-white/40 text-xs">
                          <span className="whitespace-nowrap">{article.date}</span>
                          <span className="flex items-center gap-1 whitespace-nowrap">
                            <i className="ri-eye-line"></i>
                            {article.views >= 10000 ? `${(article.views / 10000).toFixed(0)}万` : article.views}
                          </span>
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <p className="text-foreground-400 text-base">该分类暂无内容，敬请期待</p>
              </div>
            )}
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-100">
          <div className="mx-auto max-w-[1280px] px-6 md:px-10">
            <div className="max-w-2xl mx-auto text-center mb-10">
              <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-900">
                （瓜友热评）
              </h2>
            </div>

            <div className="max-w-2xl mx-auto relative">
              <div className="flex items-start gap-6">
                <div className="flex-shrink-0 mt-1">
                  <div className="px-4 py-2 bg-foreground-900 text-accent-500 rounded-full text-sm font-bold flex items-center gap-1.5 whitespace-nowrap">
                    <i className="ri-star-fill text-accent-500 text-xs"></i>
                    {t.rating}
                  </div>
                </div>
                <div className="flex-1">
                  <blockquote className="font-heading text-lg md:text-xl text-foreground-700 leading-relaxed italic">
                    &ldquo;{t.comment}&rdquo;
                  </blockquote>
                  <div className="mt-6 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full overflow-hidden">
                      <img src={t.avatar} alt={t.username} className="w-full h-full object-cover" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground-900">{t.username}</p>
                      <p className="text-xs text-foreground-400">{t.platform}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="absolute bottom-0 right-0 flex items-center gap-2">
                <button
                  onClick={() => setReviewIndex((c) => (c === 0 ? testimonials.length - 1 : c - 1))}
                  className="w-10 h-10 flex items-center justify-center rounded-lg border border-foreground-300 text-foreground-600 hover:bg-background-200 transition-colors cursor-pointer"
                  aria-label="上一条评论"
                >
                  <i className="ri-arrow-left-s-line text-lg"></i>
                </button>
                <button
                  onClick={() => setReviewIndex((c) => (c === testimonials.length - 1 ? 0 : c + 1))}
                  className="w-10 h-10 flex items-center justify-center rounded-lg bg-foreground-900 text-white hover:bg-foreground-800 transition-colors cursor-pointer"
                  aria-label="下一条评论"
                >
                  <i className="ri-arrow-right-s-line text-lg"></i>
                </button>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}